import React, { useState } from 'react';
import PlacesAutocomplete, { geocodeByAddress, Suggestion } from 'react-places-autocomplete';
import { AlertCircle } from 'lucide-react';

// List of US states with their codes
const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' }
];

interface AddressFieldsProps {
  address_line1: string;
  address_city: string;
  address_state: string;
  address_zip: string;
  onChange: (field: string, value: string) => void;
  error?: string;
  required?: boolean;
}

// Simplified type for address components
function parseAddressComponents(addressComponents: any[]) {
  let street = '';
  let city = '';
  let state = '';
  let zip = '';
  for (const component of addressComponents) {
    const types: string[] = component.types;
    if (types.includes('street_number')) {
      street = component.long_name + ' ' + street;
    } else if (types.includes('route')) {
      street += component.long_name;
    } else if (types.includes('locality')) {
      city = component.long_name;
    } else if (types.includes('administrative_area_level_1')) {
      state = component.short_name;
    } else if (types.includes('postal_code')) {
      zip = component.long_name;
    }
  }
  return { street: street.trim(), city, state, zip };
}

export function AddressFields({
  address_line1,
  address_city,
  address_state,
  address_zip,
  onChange,
  error,
  required = true
}: AddressFieldsProps) {
  const [addressInput, setAddressInput] = useState<string>(address_line1);

  const handleSelect = async (value: string) => {
    setAddressInput(value);
    onChange('address_line1', value);
    try {
      const results = await geocodeByAddress(value);
      if (results && results[0] && results[0].address_components) {
        const { street, city, state, zip } = parseAddressComponents(results[0].address_components);
        if (street) onChange('address_line1', street);
        if (city) onChange('address_city', city);
        if (state) onChange('address_state', state);
        if (zip) onChange('address_zip', zip);
      }
    } catch (err) {
      onChange('address_line1', value);
    }
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p className="text-sm">{error}</p>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className="block text-sm font-medium text-brand-secondary mb-1">
            Street Address {required && '*'}
          </label>
          <PlacesAutocomplete
            value={addressInput}
            onChange={(value: string) => {
              setAddressInput(value);
              onChange('address_line1', value);
            }}
            onSelect={handleSelect}
            searchOptions={{ componentRestrictions: { country: ['us'] } }}
          >
            {({ getInputProps, suggestions, getSuggestionItemProps, loading }: {
              getInputProps: (options: any) => React.InputHTMLAttributes<HTMLInputElement>;
              suggestions: Suggestion[];
              getSuggestionItemProps: (suggestion: Suggestion, options?: any) => any;
              loading: boolean;
            }) => (
              <div className="relative">
                <input
                  {...getInputProps({
                    placeholder: 'Start typing your address...',
                    className:
                      'w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary',
                    required,
                  })}
                />
                {suggestions.length > 0 && (
                  <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg shadow-lg mt-1">
                    {loading && <div className="px-4 py-2 text-gray-500">Loading...</div>}
                    {suggestions.map((suggestion: Suggestion) => (
                      <div
                        {...getSuggestionItemProps(suggestion, {
                          className:
                            'px-4 py-2 cursor-pointer hover:bg-gray-100' +
                            (suggestion.active ? ' bg-gray-100' : ''),
                        })}
                        key={suggestion.placeId}
                      >
                        {suggestion.description}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </PlacesAutocomplete>
        </div>
        <div>
          <label className="block text-sm font-medium text-brand-secondary mb-1">
            City {required && '*'}
          </label>
          <input
            type="text"
            value={address_city}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange('address_city', e.target.value)}
            className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
            required={required}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-brand-secondary mb-1">
            State {required && '*'}
          </label>
          <select
            value={address_state}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onChange('address_state', e.target.value)}
            className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
            required={required}
          >
            <option value="">Select State</option>
            {US_STATES.map((state) => (
              <option key={state.code} value={state.code}>
                {state.code} - {state.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-brand-secondary mb-1">
            ZIP Code {required && '*'}
          </label>
          <input
            type="text"
            value={address_zip}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const value = e.target.value;
              if (value.length <= 5 && /^\d*$/.test(value)) {
                onChange('address_zip', value);
              }
            }}
            maxLength={5}
            pattern="[0-9]{5}"
            className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
            required={required}
            placeholder="e.g., 12345"
          />
        </div>
      </div>
    </div>
  );
} 